import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Clock,
  User,
  CheckCircle,
  Trash2,
  Eye,
  Lock,
  Edit,
} from "lucide-react";
import { format } from "date-fns";
import { useDoctors } from "@/hooks/useDoctors";
import {
  getServiceName,
  getDoctorNameFlexible,
  getPatientDisplayName,
} from "@/utils/dataLookups";

export default function DailySchedule({
  date,
  appointments = [],
  unavailability = [],
  services = [],
  onComplete,
  onDeleteAppointment,
  onDeleteUnavailability,
  onEditUnavailability,
  onViewAppointment,
}) {
  // Fetch doctors data to map provider IDs to names
  const { data: allDoctors = [] } = useDoctors();

  const formatTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      return format(date, "h:mm a");
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Invalid time";
    }
  };

  const formatAppointmentEndTime = (dateTimeString, durationMinutes = 60) => {
    try {
      const startDate = new Date(dateTimeString);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      return format(endDate, "h:mm a");
    } catch (error) {
      console.error("Error formatting appointment end time:", error);
      return "Invalid time";
    }
  };

  const statusColors = {
    scheduled: "bg-blue-100 text-blue-700",
    confirmed: "bg-teal-100 text-teal-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    no_show: "bg-gray-100 text-gray-700",
  };

  const combinedSchedule = [
    ...(appointments || []).map((item) => ({
      ...item,
      type: "appointment",
      sort_date: item.appointment_date,
    })),
    ...(unavailability || []).map((item) => ({
      ...item,
      type: "unavailability",
      sort_date: item.start_time,
    })),
  ].sort((a, b) => new Date(a.sort_date) - new Date(b.sort_date));

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          {format(date, "EEEE, MMMM d, yyyy")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {combinedSchedule.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No events scheduled for this day</p>
          </div>
        ) : (
          <div className="space-y-3">
            {combinedSchedule.map((item) => {
              if (item.type === "appointment") {
                return (
                  <div
                    key={`apt-${item.id}`}
                    className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">
                            {formatTime(item.appointment_date)} -{" "}
                            {item.end_time
                              ? formatTime(`${item.date}T${item.end_time}:00`)
                              : formatAppointmentEndTime(
                                  item.appointment_date,
                                  item.duration_actual || 60
                                )}
                          </span>
                          <Badge
                            className={`${statusColors[item.status] || statusColors.scheduled} border-0 text-xs`}
                          >
                            {item.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-1">
                          {getServiceName(item.service_id, services)}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <User className="w-3 h-3" />
                          <span>Patient: {getPatientDisplayName(item)}</span>
                        </div>
                        {item.provider_name && (
                          <div className="text-xs text-slate-500">
                            Provider:{" "}
                            {getDoctorNameFlexible(
                              item.provider_name,
                              allDoctors
                            )}
                          </div>
                        )}
                        {item.room_name && (
                          <div className="text-xs text-slate-500">
                            Room: {item.room_name}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-1 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            onViewAppointment && onViewAppointment(item)
                          }
                          className="h-8 w-8"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        {item.status === "scheduled" && onComplete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onComplete(item.id)}
                            className="h-8 w-8 text-green-600 hover:text-green-700"
                          >
                            <CheckCircle className="w-3 h-3" />
                          </Button>
                        )}
                        {onDeleteAppointment && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDeleteAppointment(item.id)}
                            className="h-8 w-8 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              } else {
                // type === 'unavailability'
                return (
                  <div
                    key={`un-${item.id}`}
                    className="border border-slate-200 rounded-lg p-4 bg-slate-100"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Lock className="w-4 h-4 text-slate-600" />
                          <span className="font-medium text-slate-900">
                            {item.is_all_day
                              ? "All Day"
                              : `${formatTime(item.start_time)} - ${formatTime(item.end_time)}`}
                          </span>
                          <Badge
                            variant="secondary"
                            className="bg-gray-300 text-gray-800 border-0 text-xs"
                          >
                            Unavailable
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-1">
                          {item.reason || "No reason specified"}
                        </p>
                        {item.provider_name && (
                          <div className="text-xs text-slate-500">
                            Provider:{" "}
                            {getDoctorNameFlexible(
                              item.provider_name,
                              allDoctors
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-1 ml-2">
                        {onEditUnavailability && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEditUnavailability(item)}
                            className="h-8 w-8 text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        )}
                        {onDeleteUnavailability && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDeleteUnavailability(item.id)}
                            className="h-8 w-8 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
